/**
 * useSchoolResults – a school's quiz results and pass mark, loaded once for the
 * Classroom Management overview and the Classes tab.
 *
 * The newest results up to the service's cap; `truncated` says when older ones
 * were left out, so a screen can say its figures cover the recent part only.
 */

import { useEffect, useState } from 'react';
import { DEFAULT_PASS_THRESHOLD, type Result } from '../lib/analytics/metrics';
import { MAX_RESULTS, getPassThreshold, getSchoolResults } from '../services/performanceService';

export interface SchoolResults {
  results: Result[];
  passThreshold: number;
  loading: boolean;
  error: string | null;
  /** The newest MAX_RESULTS only; older results were left out. */
  truncated: boolean;
}

export function useSchoolResults(schoolId: string | null | undefined): SchoolResults {
  const [results, setResults] = useState<Result[]>([]);
  const [passThreshold, setPassThreshold] = useState(DEFAULT_PASS_THRESHOLD);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    setResults([]);
    setError(null);
    if (!schoolId) return;
    let cancelled = false;
    setLoading(true);
    Promise.all([getSchoolResults(schoolId), getPassThreshold(schoolId)])
      .then(([list, threshold]) => {
        if (cancelled) return;
        setResults(list);
        setPassThreshold(threshold);
      })
      .catch((err) => {
        console.error('[useSchoolResults] results could not be loaded:', err);
        if (!cancelled) setError('Quiz results could not be loaded, so activity figures are unavailable.');
      })
      .finally(() => !cancelled && setLoading(false));
    return () => {
      cancelled = true;
    };
  }, [schoolId]);

  return { results, passThreshold, loading, error, truncated: results.length >= MAX_RESULTS };
}
